/**
 * State for the 3D walk-through first run — the 3D ad's landing (see
 * `./landingFlow.ts`).
 *
 * The visitor lands on the ad's living room in the plan view with the ad's
 * hook over it. One tap ("Step inside") opens the walk-through on that room
 * and plays the ad's camera tour (`SHOWCASE_TOUR`): the plan tilts into 3D,
 * the camera drops into the room, walks in, the light goes golden, then
 * night. No sign-in and no entitlement: it runs in the browser. Then they
 * make the room theirs, walk through *it* in 3D (no account needed either),
 * and the guide's last step asks them to save it. The guide shows four steps:
 *
 *   - `example`   Step inside        — the showcase room's first 3D frame
 *   - `own_room`  Make it yours      — their own room exists (template or sketch)
 *   - `own_3d`    Your room in 3D    — the walk-through opened on their room
 *   - `save`      Save it            — an account (Google / Apple / email)
 *
 * Steps tick from product events observed through
 * {@link subscribeTrackedEvents}; the overlay and canvas don't know this
 * module exists. The 3D button on the showcase room replays the tour
 * through `setRender3dGateHook`. Mirrors `./fengShuiOnboarding.ts`.
 */

import { useSyncExternalStore } from "react";
import { getFirstTouch, getLastTouch } from "./attribution";
import { isCollabOnboardingActive } from "./collabOnboarding";
import { seedRender3dRoom } from "./demoRooms";
import { isFengShuiOnboardingActive } from "./fengShuiOnboarding";
import { setFunnelStage } from "./funnelStage";
import { isInAppBrowser } from "./inAppBrowser";
import { landingUtmContent } from "./landingFlow";
import { exitSharedRoom } from "./openRoom";
import {
  closeRender3d,
  isRender3dOpen,
  openRender3dUngated,
  setRender3dGateHook,
  type Render3dOutcome,
} from "./render3dRequests";
import { isRender3dStarterRoom, SHOWCASE_TOUR } from "./render3dShowcase";
import { requestRoomTemplates } from "./roomTemplateRequests";
import { useDesign } from "./store";
import { addBreadcrumb, setUserProperty, subscribeTrackedEvents, track } from "./telemetry";

export type Render3dStep = "example" | "own_room" | "own_3d" | "save";
export const RENDER3D_STEPS: readonly Render3dStep[] = ["example", "own_room", "own_3d", "save"];

export interface Render3dOnboardingState {
  /** The flow was entered on this device. */
  active: boolean;
  startedAt: number | null;
  /** The landing's call to action was answered ("Step inside" or "Start my own room"). */
  introSeenAt: number | null;
  /** The landing hero handed over to the guide. While `null` the hero owns the screen. */
  landingDoneAt: number | null;
  utmContent: string | null;
  /** When the showcase room was swapped for the visitor's own. */
  ownRoomStartedAt: number | null;
  /** Completion timestamps per step; `null` = not yet. */
  steps: Record<Render3dStep, number | null>;
  /** Guide closed by the user. */
  dismissed: boolean;
  /** Set once every step is done; `onboarding_completed` fires when it flips. */
  completedAt: number | null;
}

const STORAGE_KEY = "room-canvas:onboarding:render3d";

function initial(): Render3dOnboardingState {
  return {
    active: false,
    startedAt: null,
    introSeenAt: null,
    landingDoneAt: null,
    utmContent: null,
    ownRoomStartedAt: null,
    steps: { example: null, own_room: null, own_3d: null, save: null },
    dismissed: false,
    completedAt: null,
  };
}

function load(): Render3dOnboardingState {
  if (typeof window === "undefined") return initial();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initial();
    const parsed = JSON.parse(raw) as Partial<Render3dOnboardingState>;
    if (!parsed || typeof parsed !== "object") return initial();
    const base = initial();
    return { ...base, ...parsed, steps: { ...base.steps, ...(parsed.steps ?? {}) } };
  } catch {
    return initial();
  }
}

let state: Render3dOnboardingState = load();
const listeners = new Set<() => void>();

function persist(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — in-memory state still works this session */
  }
}

function update(patch: Partial<Render3dOnboardingState>): void {
  state = { ...state, ...patch };
  persist();
  for (const l of listeners) l();
}

export function getRender3dOnboarding(): Render3dOnboardingState {
  return state;
}

export function subscribeRender3dOnboarding(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useRender3dOnboarding(): Render3dOnboardingState {
  return useSyncExternalStore(subscribeRender3dOnboarding, getRender3dOnboarding, getRender3dOnboarding);
}

export function isRender3dOnboardingActive(): boolean {
  return state.active;
}

const secondsSince = (t: number | null, now: number) =>
  t != null ? Math.max(0, Math.round((now - t) / 1000)) : null;

/** Steps the guide shows. An example never watched drops out once they're in their own room. */
export function visibleRender3dSteps(s: Render3dOnboardingState = state): Render3dStep[] {
  if (s.ownRoomStartedAt == null) return [...RENDER3D_STEPS];
  return RENDER3D_STEPS.filter((k) => k !== "example" || s.steps[k] != null);
}

export function completedRender3dStepCount(s: Render3dOnboardingState = state): number {
  return visibleRender3dSteps(s).filter((k) => s.steps[k] != null).length;
}

/** The first visible step still to do, or `null` when the guide is finished. */
export function currentRender3dStep(s: Render3dOnboardingState = state): Render3dStep | null {
  return visibleRender3dSteps(s).find((k) => s.steps[k] == null) ?? null;
}

export function hasRemainingRender3dSteps(s: Render3dOnboardingState = state): boolean {
  return currentRender3dStep(s) != null;
}

/** Whether the landing hero (the ad's hook over the showcase room) owns the screen. */
export function isRender3dLandingPending(s: Render3dOnboardingState = state): boolean {
  return s.active && !s.dismissed && s.landingDoneAt == null;
}

/** Whether the guide should be on screen. */
export function isRender3dGuideVisible(s: Render3dOnboardingState = state): boolean {
  return s.active && !s.dismissed && s.landingDoneAt != null;
}

function startStatus(utmContent: string | null, source: "landing" | "handoff") {
  const inApp = isInAppBrowser();
  setUserProperty("first_run", "render_3d");
  setUserProperty("in_app_browser", inApp);
  track("render_3d_onboarding_shown", { utm_content: utmContent, in_app_browser: inApp, source });
}

/**
 * Enter the flow on the showcase room and emit the funnel head. Idempotent;
 * returns false when there was work to protect or another first run
 * already owns this device.
 */
export function enterRender3dOnboarding(opts?: { utmContent?: string | null }): boolean {
  if (state.active) return true;
  if (isCollabOnboardingActive() || isFengShuiOnboardingActive()) return false;
  const search = typeof window === "undefined" ? "" : window.location.search;
  const utmContent = opts?.utmContent ?? landingUtmContent(search, getLastTouch() ?? getFirstTouch());
  if (!seedRender3dRoom(utmContent)) return false;
  update({ ...initial(), active: true, startedAt: Date.now(), utmContent });
  ensureEventBridge();
  startStatus(utmContent, "landing");
  setFunnelStage("r3d_landing");
  addBreadcrumb("render3d-onboarding:entered", { utm_content: utmContent });
  return true;
}

/**
 * Resume a flow carried over from an in-app browser (see `handoff.ts`). A
 * visitor who was still on the landing gets the landing again, everyone
 * else the guide at the step they had reached. No-op when a flow is active.
 */
export function restoreRender3dOnboarding(carried: Partial<Render3dOnboardingState>): boolean {
  if (state.active || isCollabOnboardingActive() || isFengShuiOnboardingActive()) return false;
  const now = Date.now();
  const base = initial();
  const num = (v: unknown) => (typeof v === "number" ? v : null);
  update({
    ...base,
    active: true,
    startedAt: num(carried.startedAt) ?? now,
    utmContent: typeof carried.utmContent === "string" ? carried.utmContent : null,
    introSeenAt: num(carried.introSeenAt),
    landingDoneAt: num(carried.landingDoneAt),
    ownRoomStartedAt: num(carried.ownRoomStartedAt),
    steps: { ...base.steps, ...(carried.steps ?? {}) },
  });
  ensureEventBridge();
  startStatus(state.utmContent, "handoff");
  setFunnelStage(state.ownRoomStartedAt != null ? "r3d_my_room" : "r3d_landing");
  return true;
}

/** Tick a step (once). */
export function completeRender3dStep(step: Render3dStep): void {
  if (!state.active || state.steps[step] != null) return;
  const now = Date.now();
  update({ steps: { ...state.steps, [step]: now } });
  track("onboarding_step_completed", {
    flow: "render_3d",
    step,
    seconds_since_start: secondsSince(state.startedAt, now),
  });
  if (state.completedAt == null && currentRender3dStep() == null) {
    update({ completedAt: now });
    track("onboarding_completed", {
      flow: "render_3d",
      steps_done: completedRender3dStepCount(),
      seconds_since_start: secondsSince(state.startedAt, now),
    });
  }
}

/**
 * Open the showcase walk-through and play its tour. Free: it runs in the
 * browser on the showcase room, with no entitlement. Opens on whatever the
 * starter room looks like now, so a piece the visitor moved shows up moved.
 */
export function openRender3dExample(source: "landing" | "guide" | "handoff" | "replay"): boolean {
  if (!state.active || !isRender3dStarterRoom(useDesign.getState().design)) return false;
  if (isRender3dOpen()) closeRender3d();
  track("render_3d_example_opened", { source });
  openRender3dUngated("landing", null, {
    tour: SHOWCASE_TOUR,
    next: {
      title: "That's your floor plan, in 3D.",
      body: "Now make it your room and walk through it. No sign-in needed.",
      cta: "Try it on my room",
      pill: "My room",
      onSelect: () => {
        closeRender3d();
        finishRender3dLanding("own_room");
      },
    },
  });
  return true;
}

/** "Step inside" on the landing. */
export function acceptRender3dIntro(): boolean {
  if (!state.active) return false;
  if (state.introSeenAt == null) {
    update({ introSeenAt: Date.now() });
    track("render_3d_intro_choice", { choice: "step_inside" });
  }
  return openRender3dExample("landing");
}

/**
 * Swap the showcase room for the visitor's own. `keepCanvas`: the caller
 * is about to load a room of its own (a template), so skip the blank Shape
 * view. Ends the landing whatever state it was in.
 */
export function startRender3dOwnRoom(opts: { keepCanvas?: boolean } = {}): void {
  if (!state.active) return;
  const now = Date.now();
  update({
    ownRoomStartedAt: state.ownRoomStartedAt ?? now,
    introSeenAt: state.introSeenAt ?? now,
    landingDoneAt: state.landingDoneAt ?? now,
  });
  setFunnelStage("r3d_my_room");
  if (opts.keepCanvas) return;
  exitSharedRoom();
  useDesign.getState().resetAll();
}

/** "Start my own room" on the landing, before the example. */
export function chooseRender3dOwnRoom(): void {
  if (!state.active || state.landingDoneAt != null) return;
  if (state.introSeenAt == null) {
    update({ introSeenAt: Date.now() });
    track("render_3d_intro_choice", { choice: "own_room" });
  }
  finishRender3dLanding("own_room");
}

/**
 * Leave the landing hero for the guide. `own_room` also opens the room
 * chooser on the living rooms (the ad's room type); the chooser keeps the
 * showcase room on the canvas until a template replaces it.
 */
export function finishRender3dLanding(next: "stay" | "own_room" = "stay"): void {
  if (!state.active) return;
  if (state.landingDoneAt == null) {
    const now = Date.now();
    update({ introSeenAt: state.introSeenAt ?? now, landingDoneAt: now });
  }
  if (next === "own_room") requestRoomTemplates({ kind: "living" });
}

/** Close the guide (collapse on desktop is not a dismissal). */
export function dismissRender3dGuide(): void {
  if (!state.active || state.dismissed) return;
  const now = Date.now();
  update({ dismissed: true, introSeenAt: state.introSeenAt ?? now, landingDoneAt: state.landingDoneAt ?? now });
  track("onboarding_guide_done", { flow: "render_3d", steps_done: completedRender3dStepCount() });
}

/** "Skip guide": leave the flow and keep whatever room is on the canvas. */
export function exitRender3dOnboarding(method: "skip"): void {
  if (!state.active || state.dismissed) return;
  const stepsDone = completedRender3dStepCount();
  const now = Date.now();
  update({ dismissed: true, introSeenAt: state.introSeenAt ?? now, landingDoneAt: state.landingDoneAt ?? now });
  track("onboarding_exited", { flow: "render_3d", method, steps_done: stepsDone });
}

/** Bring a dismissed guide back (Settings → Getting started guide). */
export function reopenRender3dGuide(): void {
  if (!state.active || !state.dismissed) return;
  const now = Date.now();
  update({ dismissed: false, introSeenAt: state.introSeenAt ?? now, landingDoneAt: state.landingDoneAt ?? now });
}

// ---------------------------------------------------------------------------
// The 3D button while the flow is on
// ---------------------------------------------------------------------------

/**
 * On the showcase room the 3D button replays its tour (it's the example);
 * on any other room the walk-through opens as usual.
 */
function gate(): Render3dOutcome | null {
  if (!state.active || !isRender3dStarterRoom(useDesign.getState().design)) return null;
  return openRender3dExample("guide") ? "example" : null;
}

// ---------------------------------------------------------------------------
// Event bridge — product events → guide steps
// ---------------------------------------------------------------------------

let bridgeUnsub: (() => void) | null = null;

export function ensureEventBridge(): void {
  setRender3dGateHook(gate);
  if (bridgeUnsub) return;
  bridgeUnsub = subscribeTrackedEvents((name, props) => {
    if (!state.active) return;
    if (name === "render_3d_example_ready") {
      completeRender3dStep("example");
      setFunnelStage("r3d_example");
    } else if (name === "room_created" && state.ownRoomStartedAt != null) {
      completeRender3dStep("own_room");
    } else if (name === "render_3d_opened" && props?.own_room === true) {
      completeRender3dStep("own_3d");
      setFunnelStage("r3d_own_3d");
    } else if (name === "sign_up" || name === "login" || name === "account_linked") {
      completeRender3dStep("save");
      setFunnelStage("r3d_done");
    } else if (name === "save_prompt_shown" && props?.surface === "render_3d_guide") {
      setFunnelStage("r3d_save");
    }
  });
}

// Boot: if a previous page load entered the flow, keep listening.
if (state.active) ensureEventBridge();

/** Test-only: wipe memory + storage. */
export function _resetRender3dOnboardingForTests(): void {
  if (bridgeUnsub) bridgeUnsub();
  bridgeUnsub = null;
  setRender3dGateHook(null);
  state = initial();
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  listeners.clear();
}
